import { URLS } from '@/assets/environment/urls'
import axios from 'axios'

const TOKEN_REQUEST_TIMEOUT_MS = 15_000
const TOKEN_ENDPOINT = `${URLS.KC_URL}realms/${URLS.KC_REALM}/protocol/openid-connect/token`

type OAuthTokenPayload = {
	access_token?: unknown
	refresh_token?: unknown
	expires_in?: unknown
	refresh_expires_in?: unknown
	token_type?: unknown
}

export type OAuthTokenResult = {
	accessToken: string
	refreshToken: string | null
	accessExpiresAt: number | null
	refreshExpiresAt: number | null
	receivedAt: number
	tokenType: string | null
}

export type OAuthTokenRequestFailureKind = 'terminal' | 'transient'

export class OAuthTokenRequestError extends Error {
	constructor(
		public readonly kind: OAuthTokenRequestFailureKind,
		message: string
	) {
		super(message)
		this.name = 'OAuthTokenRequestError'
	}
}

const optionalNonEmptyString = (value: unknown): string | null =>
	typeof value === 'string' && value.trim().length > 0 ? value : null

const deriveAbsoluteExpiry = (seconds: unknown, requestedAt: number): number | null => {
	if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) return null

	// Keycloak's lifetime starts before the response reaches us. Anchoring it to
	// the request (and rounding down) avoids extending a token because of latency.
	const lifetimeMs = Math.floor(seconds * 1_000)
	const expiresAt = requestedAt + lifetimeMs

	return Number.isSafeInteger(lifetimeMs) && Number.isSafeInteger(expiresAt) ? expiresAt : null
}

const parseTokenResponse = (
	payload: unknown,
	requestedAt: number,
	receivedAt: number
): OAuthTokenResult | null => {
	if (!payload || typeof payload !== 'object') return null

	const tokenPayload = payload as OAuthTokenPayload
	const accessToken = optionalNonEmptyString(tokenPayload.access_token)
	if (!accessToken) return null

	return {
		accessToken,
		refreshToken: optionalNonEmptyString(tokenPayload.refresh_token),
		accessExpiresAt: deriveAbsoluteExpiry(tokenPayload.expires_in, requestedAt),
		refreshExpiresAt: deriveAbsoluteExpiry(tokenPayload.refresh_expires_in, requestedAt),
		receivedAt,
		tokenType: optionalNonEmptyString(tokenPayload.token_type),
	}
}

const tokenFailureKind = (error: unknown): OAuthTokenRequestFailureKind => {
	if (!error || typeof error !== 'object') return 'transient'
	const response = (error as { response?: unknown }).response
	if (!response || typeof response !== 'object') return 'transient'
	const typedResponse = response as { status?: unknown; data?: unknown }
	const data = typedResponse.data
	const oauthError =
		data && typeof data === 'object' && typeof (data as { error?: unknown }).error === 'string'
			? (data as { error: string }).error
			: null
	// Only invalid_grant definitively invalidates this persisted grant. Client,
	// scope, proxy, and authorization failures can be deployment/configuration
	// incidents; deleting the refresh credential would turn an outage into logout.
	return oauthError === 'invalid_grant' ? 'terminal' : 'transient'
}

const requestTokens = async (
	params: URLSearchParams,
	failureMessage: string
): Promise<OAuthTokenResult | null> => {
	const requestedAt = Date.now()

	try {
		const response = await axios.post(TOKEN_ENDPOINT, params.toString(), {
			timeout: TOKEN_REQUEST_TIMEOUT_MS,
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
		})

		const tokens = parseTokenResponse(response.data, requestedAt, Date.now())
		if (!tokens) {
			throw new OAuthTokenRequestError(
				'transient',
				'Token endpoint returned an invalid response'
			)
		}
		return tokens
	} catch (error) {
		// Axios errors can contain request and response bodies, including credentials.
		console.error(failureMessage)
		if (error instanceof OAuthTokenRequestError) throw error
		throw new OAuthTokenRequestError(tokenFailureKind(error), failureMessage)
	}
}

export class KcProxy {
	public static exchangeAuthorizationCode = async ({
		code,
		codeVerifier,
		redirectUri,
	}: {
		code: string
		codeVerifier: string
		redirectUri: string
	}): Promise<OAuthTokenResult | null> => {
		const params = new URLSearchParams({
			grant_type: 'authorization_code',
			code,
			client_id: URLS.KC_USER,
			redirect_uri: redirectUri,
			code_verifier: codeVerifier,
		})

		return requestTokens(params, 'Error exchanging authorization code')
	}

	public static refreshTokens = async (
		refreshToken: string
	): Promise<OAuthTokenResult | null> => {
		const params = new URLSearchParams({
			grant_type: 'refresh_token',
			refresh_token: refreshToken,
			client_id: URLS.KC_USER,
		})

		return requestTokens(params, 'Error refreshing tokens')
	}

	public static refreshAccessToken = async (refreshToken: string): Promise<string | null> => {
		const tokens = await KcProxy.refreshTokens(refreshToken)
		return tokens?.accessToken ?? null
	}
}
